import type { UtmData } from "./types";
import { getCookie, setCookie } from "./cookie";
import { log } from "./config";

const UTM_COOKIE_KEY = "mai_utm";
const UTM_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 天（秒）
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const EMPTY_UTM: UtmData = {
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  utm_term: "",
};

/** 从 URL 获取 UTM 参数 */
export function getUtmFromUrl(): UtmData {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
    utm_term: params.get("utm_term") || "",
  };
}

/** 从 cookie 获取 UTM 参数（URLSearchParams 格式，与 Theme Extension 一致） */
export function getUtmFromCookie(): UtmData {
  const raw = getCookie(UTM_COOKIE_KEY);
  if (!raw) return { ...EMPTY_UTM };

  try {
    const params = new URLSearchParams(raw);
    return {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",
    };
  } catch {
    return { ...EMPTY_UTM };
  }
}

/** 保存 UTM 到 cookie（URLSearchParams 格式，与 Theme Extension 一致） */
export function saveUtmToCookie(utm: UtmData) {
  if (!hasUtm(utm)) return;

  const str = UTM_KEYS.filter((k) => utm[k])
    .map((k) => `${k}=${encodeURIComponent(utm[k])}`)
    .join("&");

  setCookie(UTM_COOKIE_KEY, str, UTM_COOKIE_MAX_AGE);
  log("UTM saved to cookie:", utm);
}

/** 检查是否有 UTM 参数 */
export function hasUtm(utm: UtmData): boolean {
  return Object.values(utm).some((v) => v !== "");
}

/** 获取当前 UTM（URL 优先，cookie 次之） */
export function getCurrentUtm(): UtmData {
  const urlUtm = getUtmFromUrl();
  if (hasUtm(urlUtm)) return urlUtm;
  return getUtmFromCookie();
}

/** 获取 traffic 数据（用于事件 payload） */
export function getTrafficData() {
  const utm = getCurrentUtm();
  return {
    source: utm.utm_source,
    medium: utm.utm_medium,
    campaign: utm.utm_campaign,
    content: utm.utm_content,
    term: utm.utm_term,
  };
}

/** UTM 初始化：采集 + 存储 */
export function initUtmTracking() {
  const urlUtm = getUtmFromUrl();

  if (hasUtm(urlUtm)) {
    log("Found UTM in URL:", urlUtm);
    saveUtmToCookie(urlUtm);
  } else {
    const cookieUtm = getUtmFromCookie();
    if (hasUtm(cookieUtm)) {
      log("Using UTM from cookie:", cookieUtm);
    }
  }
}
